import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { fetchMyStore, type MyStore } from '@/services/siteApi';
import { updateCreatorPage } from '@/services/creatorApi';
import { useSubscription } from '@/hooks/useSubscription';

// "Your store on your site" on /my-site. With a creator store: the embed code
// (a small script from Plus that draws the product cards; checkout stays on
// Plus). Without one: open it right here, without leaving the page. A member
// who isn't a Creator yet activates the free Creator account with one button,
// then names the store; a Creator with no store yet just names it. Either way
// the embed code appears in place. Joe, Oct 25 2026: don't hide the block for
// people without a Creator account, give them the way in.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const panel: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 24 };
const h2: React.CSSProperties = { margin: 0, fontFamily: 'var(--hj-font-display)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.01em', color: INK };
const small: React.CSSProperties = { fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.45 };
const quiet: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 40, padding: '8px 14px', borderRadius: 10, fontFamily: 'inherit', fontSize: 14, fontWeight: 500, background: '#fff', color: INK, border: '1px solid var(--hj-hairline)', cursor: 'pointer', textDecoration: 'none', flexShrink: 0 };
const primary: React.CSSProperties = { ...quiet, fontWeight: 600, background: INK, color: '#fff', border: '1px solid ' + INK };
const pre: React.CSSProperties = { background: 'var(--hj-surface-1)', borderRadius: 10, padding: 12, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre', margin: 0 };
const field: React.CSSProperties = { flex: '1 1 200px', minWidth: 0, padding: '9px 12px', borderRadius: 10, border: '1px solid var(--hj-hairline)', fontSize: 14, fontFamily: 'inherit', background: '#fff', color: INK };
const off = (s: React.CSSProperties, o: boolean): React.CSSProperties => (o ? { ...s, opacity: 0.45, cursor: 'not-allowed' } : s);

const SiteStorePanel: React.FC<{ onLoaded?: (s: MyStore | null) => void }> = ({ onLoaded }) => {
  const { canSellProducts, loading: subLoading, activateCreator } = useSubscription();
  const [store, setStore] = useState<MyStore | null | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState<'idle' | 'activating' | 'naming'>('idle');
  const [justActivated, setJustActivated] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const load = () => fetchMyStore().then((r) => { setStore(r.store); onLoaded?.(r.store); }).catch(() => setStore(null));
  useEffect(() => { load(); }, []);
  if (store === undefined) return null;

  // Step 1 for a non-Creator: the free Creator account, same call as My Store's
  // "Start selling" button. The hook refreshes the subscription, so canSellProducts
  // flips and the naming step shows.
  const activate = async () => {
    setBusy('activating'); setErr(null);
    const ok = await activateCreator();
    if (!ok) setErr('We couldn’t activate your Creator account just now. Try again in a minute.');
    else setJustActivated(true);
    setBusy('idle');
  };
  // Step 2: a name makes the store (slug from the name, unique on the server),
  // then the embed code is ready and the prompt card picks the store up too.
  const create = async () => {
    const displayName = name.trim();
    if (displayName.length < 2) { setErr('Give your store a name.'); return; }
    setBusy('naming'); setErr(null);
    try { await updateCreatorPage({ displayName }); await load(); setOpen(true); }
    catch (e) { setErr((e as Error).message || 'We couldn’t open the store. Try again.'); }
    setBusy('idle');
  };

  return (
    <section className="flex flex-col" style={{ ...panel, gap: 12 }}>
      <h2 style={h2}>Your store on your site</h2>
      {!store ? (
        !canSellProducts && !subLoading ? (
          <>
            <p style={small}>Sell classes, coaching and videos from your own website. A Hey Joe Creator account is free; we take a small commission on sales. Activate it here, name your store, and the cards go on your site.</p>
            <button type="button" disabled={busy !== 'idle'} onClick={activate} style={off({ ...primary, alignSelf: 'flex-start' }, busy !== 'idle')}>
              {busy === 'activating' ? <><Loader2 className="w-4 h-4 animate-spin" /> Activating</> : 'Start selling — Free'}
            </button>
            {err && <p style={{ ...small, color: 'var(--hj-pink-ink)' }}>{err}</p>}
          </>
        ) : (
          <>
            <p style={small}>{justActivated ? 'Your Creator account is on. ' : ''}Name your store and it opens on Hey Joe Plus, ready to put on your site. You can change the name and add what you sell in My Store any time.</p>
            <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
              <input type="text" value={name} onChange={(e) => { setName(e.target.value); if (err) setErr(null); }} placeholder="Your name, or a studio name" aria-label="Store name" maxLength={80}
                onKeyDown={(e) => { if (e.key === 'Enter') create(); }} style={field} />
              <button type="button" disabled={busy !== 'idle' || subLoading} onClick={create} style={off(primary, busy !== 'idle' || subLoading)}>
                {busy === 'naming' ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening</> : 'Open my store'}
              </button>
            </div>
            {err && <p style={{ ...small, color: 'var(--hj-pink-ink)' }}>{err}</p>}
            <Link to="/my-store" style={{ ...small, color: INK }}>Or set it up in My Store</Link>
          </>
        )
      ) : (
        <>
          <p style={small}>
            {store.products ? `${store.products} ${store.products === 1 ? 'thing' : 'things'} on sale in ` : 'Nothing on sale yet in '}
            <a href={store.url} target="_blank" rel="noreferrer" style={{ color: INK, fontWeight: 600 }}>{store.name}</a>.
            {' '}Paste the code where the cards should go. They draw themselves, match your page, and send buyers to checkout on Hey Joe Plus.
          </p>
          {store.hidden && <p style={{ ...small, color: 'var(--hj-pink-ink)' }}>Your store is hidden right now, so the cards won’t show until you make it visible in My Store.</p>}
          {!open ? (
            <button type="button" onClick={() => setOpen(true)} style={{ ...primary, alignSelf: 'flex-start' }}>Add my store</button>
          ) : (
            <>
              <pre style={pre}>{store.snippet}</pre>
              <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
                <button type="button" style={primary} onClick={() => { navigator.clipboard?.writeText(store.snippet).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => {}); }}>
                  {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}{copied ? 'Copied' : 'Copy the code'}
                </button>
                <Link to="/my-store" style={{ ...small, color: INK }}>Manage my store</Link>
              </div>
              <p style={small}>Sales from your site count as your own, so you keep more of each one. Change what’s on sale in My Store; your site updates on its own.</p>
            </>
          )}
          {open && <a href={store.url} target="_blank" rel="noreferrer" style={{ ...small, color: INK, display: 'inline-flex', alignItems: 'center', gap: 4 }}>See my store on Plus <ExternalLink size={13} aria-hidden="true" /></a>}
        </>
      )}
    </section>
  );
};

export default SiteStorePanel;
